import { EditorState } from "draft-js";
import { convertFromHTML, convertToHTML } from "draft-convert";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { BrokerProps, ProfileProps } from "types";
import { useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import {
  capitalizeWords,
  getApiPayloadFromForm,
  stripUnsupportedTags,
} from "utils";
import { useApiCall } from "hooks";
import { patchProfileAPI } from "services";
import { GlobalContext } from "context";
import { SET_LOADING_MODAL } from "constant";
import { SolButton } from "@solstice/sol-react";
import { CustomEditor } from "ui-molecules";
import { Tooltip } from "react-tooltip";

interface ContentEditorProps {
  profile: ProfileProps | null;
  setProfile: any;
  broker?: BrokerProps | null;
  reloadBroker: () => void;
  profile_key: keyof ProfileProps;
  label?: string /** Label to display in case profile_key is not suitable (for example awards_html) */;
  initialValue?: string /** Initial value to populate the editor in case it cannot be obtained from the profile_key */;
}

const ContentEditor: React.FC<ContentEditorProps> = ({
  profile,
  setProfile,
  broker,
  reloadBroker,
  profile_key,
  initialValue,
  label,
}) => {
  const { dispatch } = useContext(GlobalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [patchProfile] = useApiCall(patchProfileAPI);
  const profile_key_display = label ? label : capitalizeWords(profile_key, "_");

  const saveDraftTooltip = useMemo((): string => {
    if (!broker?.update_timestamp)
      return `${profile_key_display} has not been saved`;
    const updatedDatetime = new Date(broker.update_timestamp).toLocaleString();
    let author: string | null = null;
    if (broker?.updated_user_first_name) {
      author = broker.updated_user_first_name;
      if (broker?.updated_user_last_name) {
        author += ` ${broker.updated_user_last_name}`;
      }
    } else if (broker?.updated_user_email) {
      author = broker.updated_user_email;
    }
    const tooltipMessage = author
      ? `Last Saved by ${author} on ${updatedDatetime}`
      : `Last Saved on ${updatedDatetime}`;
    return tooltipMessage;
  }, [broker, profile_key_display]);

  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(
      convertFromHTML(
        stripUnsupportedTags(
          initialValue
            ? initialValue
            : profile
            ? profile[profile_key] ?? ""
            : ""
        )
      )
    )
  );

  const handleSaveDraft = () => {
    const contentState = editorState.getCurrentContent();
    const rawContentState = convertToHTML(contentState as any);
    const contentString = stripUnsupportedTags(rawContentState.toString());

    const payload = getApiPayloadFromForm(profile, {
      [profile_key]: contentString,
    });
    if (!payload || !Object.keys(payload)?.length) {
      searchParams.set("mode", "general");
      setSearchParams(searchParams);
      return;
    }

    dispatch({
      type: SET_LOADING_MODAL,
      payload: {
        open: true,
        title: `Saving ${profile_key_display} content`,
      },
    });

    patchProfile({
      pk: profile?.id,
      [profile_key]: contentString,
    })
      .then((res: any) => {
        if (res) {
          setProfile(res);
          reloadBroker();
        }
      })
      .finally(() => {
        dispatch({
          type: SET_LOADING_MODAL,
          payload: {
            open: false,
            title: "",
          },
        });
        searchParams.set("mode", "general");
        setSearchParams(searchParams);
      });
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-row items-center justify-between mb-6">
          <h2 className="text-xl text-jll-text-base-subdued">
            {profile_key_display}
          </h2>
          <div className="flex flex-row items-center space-x-4">
            <SolButton
              variant="tertiary"
              onSol-click={() => {
                searchParams.set("mode", "general");
                setSearchParams(searchParams);
              }}
            >
              Cancel
            </SolButton>
            <SolButton
              variant="secondary"
              onSol-click={handleSaveDraft}
              data-tooltip-id={`${profile_key}_save_draft_tooltip`}
              data-tooltip-content={saveDraftTooltip}
            >
              Save draft
            </SolButton>
            <Tooltip id={`${profile_key}_save_draft_tooltip`} />
          </div>
        </div>

        <div className="border border-jll-light-stroke-default rounded mb-6">
          <CustomEditor
            editorState={editorState}
            setEditorState={setEditorState}
          />
        </div>
      </div>
    </>
  );
};

export default ContentEditor;
